import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HeartbeatIcon as Heartbeat, ShieldCheckIcon as ShieldCheck, LockKeyIcon as LockKey, ArrowRightIcon as ArrowRight, FilesIcon as Files, PillIcon as Pill, MagnifyingGlassIcon as MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { isNativeApp } from "@/lib/nativeBridge";
import IapUpgradeButton from "@/components/IapUpgradeButton";

/**
 * Public-view health-files card.
 *
 * Anonymous: shows the *count* of records on file + which classes
 * exist (cert / panel / OFA hips), but never the records themselves.
 *
 * Buyer Pass holders / admins / owners: tap "View files" to expand
 * the list (server-side gated by /public/dogs/{id}/health-files which
 * 402s without an active Buyer Pass).
 *
 * The component is the gentle-paywall surface: visitors see that the
 * dog has health files (quality signal) and a clear "Unlock with
 * Buyer Pass →" path.
 */
export default function HealthFilesCard({ dogId, summary }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [error, setError] = useState(null);

  const recordsCount = summary?.records_count || 0;
  const hasCerts = !!summary?.has_certificates;
  const hasPanel = !!summary?.has_genetic_panel;
  const hasHips = !!summary?.has_ofa_hips;
  const empty = recordsCount === 0 && !hasCerts && !hasPanel && !hasHips;

  const open = async () => {
    setExpanded(true);
    if (files || loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/public/dogs/${dogId}/health-files`);
      setFiles(r.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Anonymous viewer — invite to sign in / register.
        setPaywall("anon");
      } else if (status === 402) {
        setPaywall("buyer_pass");
      } else {
        setError(err?.response?.data?.detail || "Could not load health files.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (empty) {
    return (
      <div
        className="rounded-xl border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-5"
        data-testid="health-files-card-empty"
      >
        <div className="flex items-center gap-2 text-[#6B6B6B]">
          <Heartbeat size={16} weight="duotone" />
          <p className="text-[12.5px]">
            No public health files on this dog yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-[#E5D08A] bg-[#FBF7EC] p-5"
      data-testid="health-files-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Heartbeat size={16} weight="duotone" className="text-[#8A6A00]" />
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00]">
            Health files on record
          </p>
        </div>
        <p className="text-[11px] text-[#6B6B6B]">
          {recordsCount > 0 && <span data-testid="health-records-count">{recordsCount} record{recordsCount === 1 ? "" : "s"}</span>}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <BadgeRow ok={hasCerts} icon={ShieldCheck} label="Health certificate" />
        <BadgeRow ok={hasPanel} icon={Files} label="Genetic panel" />
        <BadgeRow ok={hasHips} icon={MagnifyingGlass} label="OFA hips" />
        <BadgeRow ok={recordsCount > 0} icon={Pill} label={`${recordsCount} record${recordsCount === 1 ? "" : "s"}`} />
      </div>

      {!expanded && (
        <Button
          type="button"
          onClick={open}
          size="sm"
          variant="outline"
          className="mt-4 rounded-full border-[#0E0E0E] text-ink hover:bg-[#0E0E0E] hover:text-white"
          data-testid="health-files-view-btn"
        >
          <LockKey size={13} className="mr-1.5" weight="duotone" />
          View health files
        </Button>
      )}

      {expanded && loading && (
        <p className="text-[12px] text-[#6B6B6B] mt-4">Loading…</p>
      )}

      {expanded && paywall === "anon" && (
        <PaywallBlock
          icon={LockKey}
          headline="Sign in to view health files"
          body="Health certificates, genetic panels, and full medical history are visible to signed-in members."
          ctaLabel="Sign in"
          ctaTo="/login"
          ctaTestId="health-files-signin-cta"
          secondaryLabel="Create free account"
          secondaryTo="/register"
        />
      )}

      {expanded && paywall === "buyer_pass" && (
        <PaywallBlock
          icon={LockKey}
          headline={isNativeApp() ? "Available with an active buyer plan" : "Unlock with Buyer Pass"}
          body={isNativeApp()
            ? "Health records, vet certificates, and genetic tests on every dog in the registry — plus matchmaker COI, watchlists, and saved searches — are included with an active buyer plan."
            : "Buyer Pass holders see every health record, vet certificate, and genetic test on every dog in the registry — plus matchmaker COI, watchlists, and saved searches."}
          ctaLabel={isNativeApp() ? "My account" : "Get Buyer Pass"}
          ctaTo="/billing"
          ctaTestId="health-files-buyer-pass-cta"
          iapExtra={
            isNativeApp() ? (
              <IapUpgradeButton
                packageIds={["buyer_pass_monthly", "buyer_pass_annual"]}
                label="Unlock with Buyer Pass"
                context="health_files"
                fallbackTo="/billing"
                fallbackLabel="My account"
                className="w-full"
              />
            ) : null
          }
        />
      )}

      {expanded && error && (
        <p className="text-[12px] text-crimson mt-4" data-testid="health-files-error">
          {error}
        </p>
      )}

      {expanded && files && (
        <div className="mt-4 space-y-3" data-testid="health-files-records">
          {files.health_records?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1.5">
                Records
              </p>
              <ul className="space-y-1.5 text-[13px]">
                {files.health_records.map((rec) => (
                  <li key={rec.id} className="flex items-baseline gap-2 text-ink">
                    <Pill size={12} className="text-[#8A6A00] flex-shrink-0" />
                    <span className="font-medium">{rec.type}</span>
                    <span className="text-[#6B6B6B] text-[12px]">
                      {rec.description}{rec.date ? ` · ${rec.date}` : ""}{rec.vet ? ` · ${rec.vet}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {files.health_certificates && (
            <Detail label="Certificate" value={files.health_certificates} />
          )}
          {files.genetic_panel && (
            <Detail label="Genetic panel" value={files.genetic_panel} />
          )}
          {files.ofa_hips && (
            <Detail label="OFA hips" value={files.ofa_hips} />
          )}
          <p className="text-[10px] text-[#6B6B6B] uppercase tracking-[0.18em]">
            Viewing as: {files.viewer === "owner" ? "owner" : files.viewer === "admin" ? "admin" : "Buyer Pass"}
          </p>
        </div>
      )}
    </div>
  );
}

function BadgeRow({ ok, icon: Icon, label }) {
  return (
    <div
      className={`flex items-center gap-1.5 text-[12px] rounded-md px-2.5 py-1.5 border ${
        ok
          ? "bg-white border-[#E5D08A] text-[#0E0E0E]"
          : "bg-transparent border-[#E5D08A]/40 text-[#6B6B6B]"
      }`}
    >
      <Icon size={13} weight={ok ? "duotone" : "regular"} className={ok ? "text-[#0F8043]" : "text-[#6B6B6B]"} />
      <span>{label}</span>
      <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.16em]">
        {ok ? "Yes" : "—"}
      </span>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B]">
        {label}
      </p>
      <p className="text-[13px] text-ink leading-relaxed">{value}</p>
    </div>
  );
}

function PaywallBlock({ icon: Icon, headline, body, ctaLabel, ctaTo, ctaTestId, secondaryLabel, secondaryTo, iapExtra }) {
  return (
    <div className="mt-4 rounded-lg border border-[#0E0E0E] bg-[#0E0E0E] text-white p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={15} weight="duotone" className="text-gold" />
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold">
          Buyer Pass perk
        </p>
      </div>
      <p className="font-spectral text-[18px] tracking-tight mb-1">{headline}</p>
      <p className="text-[12.5px] text-white/80 leading-relaxed mb-3">{body}</p>
      {iapExtra ? (
        <div className="mb-3 rounded-lg bg-white p-3">{iapExtra}</div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button asChild size="sm" className="rounded-full bg-[#C9A227] hover:bg-[#FFD550] text-ink">
          <Link to={ctaTo} data-testid={ctaTestId}>
            {ctaLabel} <ArrowRight size={13} className="ml-1" weight="bold" />
          </Link>
        </Button>
        {secondaryLabel && secondaryTo && (
          <Button asChild size="sm" variant="ghost" className="rounded-full text-white hover:bg-white/10">
            <Link to={secondaryTo}>{secondaryLabel}</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
